import React from 'react';
import { Server, Globe, Activity, Cpu, HardDrive, Database } from 'lucide-react';
import '../../styles/dashboard.css';

const DeploymentCard = ({ path, appInfo, onClick, user }) => {
  const appName = appInfo.appName || path.split('/').pop();
  const ownerName = appInfo.owner || 'Unknown';
  const isContainerized = appInfo.type === 'docker' || appInfo.type === 'compose';
  const isCompose = appInfo.type === 'compose';
  const status = appInfo.status || 'unknown';
  const isRunning = status === 'running';
  const isStatic = appInfo.type === 'static' || status === 'static' || appInfo.metadata?.type === 'static';
  
  const formatBytesValue = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const storageBytes = appInfo.resources?.storageUsage || appInfo.storageUsage || appInfo.metadata?.storageUsage || 0;
  const cpuPercent = appInfo.resources?.cpuPercent || 0;
  const memUsage = appInfo.resources?.memUsage || 0;
  const memLimit = appInfo.resources?.memLimit || 0;
  
  const memPercent = memLimit > 0 ? (memUsage / memLimit) * 100 : 0;

  const getVolumeCount = () => {
    if (appInfo.resources?.volumes && Array.isArray(appInfo.resources.volumes)) {
      return appInfo.resources.volumes.filter(v => (v.destination || v.path) !== '/home/app').length;
    }
    const rawVolumes = appInfo.metadata?.volumes || appInfo.volumes || [];
    if (Array.isArray(rawVolumes) && rawVolumes.length > 0) {
      return rawVolumes.filter(v => {
        if (typeof v === 'string') return !v.includes(':/home/app');
        if (typeof v === 'object' && v) return (v.path || (v.mount ? v.mount.split(':')[1] : '')) !== '/home/app';
        return true;
      }).length;
    }
    return appInfo.metadata?.volume ? 1 : 0;
  };
  const volumeCount = getVolumeCount();
  const getRelativeTime = (ts) => {
    if (!ts) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const sec = Math.floor((now - d) / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  };
  const timeAgo = getRelativeTime(appInfo.metadata?.createdAt || appInfo.createdAt || appInfo.metadata?.deployedAt || appInfo.deployedAt || appInfo.metadata?.timestamp);

  return (
    <div className="deployment-compact-card" onClick={() => onClick(path)}>
      <div className="dcc-header">
        <div className="dcc-top-row">
          <div className="dcc-title-group">
            <div className="dcc-icon">
              {appInfo.type === 'static' ? <Globe size={20} /> : <Server size={20} />}
            </div>
            <div className="dcc-title-area">
              <h3 className="dcc-name">
                {appInfo.metadata?.displayName || appName}
              </h3>
              <div className="dcc-domain" title={appInfo.domain || 'No Domain Configured'}>
                {appInfo.domain || (appInfo.port ? `${window.location.hostname}:${appInfo.port}` : 'No Domain Configured')}
              </div>
            </div>
          </div>
          <div className={`dcc-status ${isRunning ? 'running' : (status === 'static' ? 'static' : 'stopped')}`}>
            <span className="dcc-status-dot"></span>
            {isRunning ? 'Running' : (status === 'static' ? 'Static' : status)}
          </div>
        </div>
        <div className="dcc-tags" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.45rem' }}>
          <span
            className="dcc-tag"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              background: ownerName === user?.username ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(16, 185, 129, 0.12)',
              color: ownerName === user?.username ? 'var(--primary)' : '#10b981',
              border: `1px solid ${ownerName === user?.username ? 'rgba(var(--primary-rgb), 0.25)' : 'rgba(16, 185, 129, 0.25)'}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              padding: '0.2rem 0.55rem',
              borderRadius: '6px'
            }}
          >
            👤 Deployed by: @{ownerName}{ownerName === user?.username ? ' (You)' : ''}
          </span>
          {timeAgo && (
            <span
              className="dcc-tag"
              style={{
                fontSize: '0.72rem',
                color: 'var(--text-muted)',
                background: 'rgba(255,255,255,0.04)',
                padding: '0.2rem 0.5rem',
                borderRadius: '6px'
              }}
            >
              🕒 {timeAgo}
            </span>
          )}
          {isCompose && <span className="dcc-tag">Compose</span>}
          {isContainerized && !isCompose && <span className="dcc-tag">Docker</span>}
          {isStatic && (
            <span
              className="dcc-tag"
              style={{
                border: '1px solid rgba(59, 130, 246, 0.3)',
                color: '#3b82f6',
                background: 'rgba(59, 130, 246, 0.08)'
              }}
            >
              ⚡ Static Site
            </span>
          )}
          {(appInfo.isSSR || appInfo.metadata?.isSSR) && <span className="dcc-tag ssr-tag">SSR</span>}
          {volumeCount > 0 && (
            <span className="dcc-tag" title={`${volumeCount} Persistent Volume${volumeCount > 1 ? 's' : ''} Attached`} style={{ border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10b981', background: 'rgba(16, 185, 129, 0.08)' }}>
              💾 {volumeCount} {volumeCount === 1 ? 'Vol' : 'Vols'}
            </span>
          )}
        </div>
      </div>

      <div className="dcc-body">
        {isStatic ? (
          <div className="dcc-resources">
            <div className="dcc-res-item">
              <HardDrive size={14} /> <span>Size: {formatBytesValue(storageBytes)}</span>
            </div>
          </div>
        ) : (
          <div className="dcc-resources">
            <div className="dcc-res-item">
              <Cpu size={14} /> <span>CPU: {cpuPercent.toFixed(1)}%</span>
            </div>
            <div className="dcc-res-item">
              <Activity size={14} /> <span>RAM: {formatBytesValue(memUsage)}</span>
            </div>
            <div className="dcc-res-item">
              <HardDrive size={14} /> <span>Disk: {formatBytesValue(storageBytes)}</span>
            </div>
          </div>
        )}
        
        {appInfo.additionalEntryPoints && appInfo.additionalEntryPoints.length > 0 && (
          <div className="dcc-extra-endpoints" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.05))' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>ADDITIONAL ENTRY POINTS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {appInfo.additionalEntryPoints.map((ep, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#60a5fa' }}>
                  <Globe size={12} />
                  <span title={ep.domain || `${window.location.hostname}:${ep.port}`}>
                    {ep.domain || `${window.location.hostname}:${ep.port}`}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                    ({String(ep.role || (ep.type === 'static' ? 'Static' : 'App')).replace(/[-_]/g, ' ')})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeploymentCard;
